import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import LandingStep from './LandingStep';
import PatientStatusStep from './PatientStatusStep';
import AppointmentTypeStep from './AppointmentTypeStep';
import AppointmentTypeZipStep from './AppointmentTypeZipStep';
import ProviderSelectionStep from './ProviderSelectionStep';
import PrequalificationStep from './PrequalificationStep';
import PatientInfoStep from './PatientInfoStep';
import ConfirmationStep from './ConfirmationStep';
import SuccessStep from './SuccessStep';
import ExistingAppointmentsStep from './ExistingAppointmentsStep';
import { PatientInfoFormData } from './PatientInfoStep';
import ManageAppointmentStep from './ManageAppointmentStep';
import EditAppointmentRouter from './EditAppointmentRouter';
import { getOpenSlots, getProviderSpecificOpenSlots } from '../../utils/apis';
import { useQuery } from 'react-query';

// Type definitions for better type safety
interface Provider {
  providerId: string;
  displayName: string;
  schedulingName: string;
  firstName: string;
  lastName: string;
  specialty: string;
  departmentId: string;
  departmentName: string;
  departmentZip: string;
  departmentAddress: string;
  departmentCity: string;
  departmentState: string;
  availableSlots: AppointmentSlot[];
}

interface AppointmentSlot {
  startTime: string;
  endTime: string;
  duration: number;
  appointmentId: string;
  appointmentName: string;
  appointmentTypeId: string;
}

interface ExistingAppointment {
  appointmentid: string;
  patientid: string;
  providerid: string;
  departmentid: string;
  appointmenttypeid: string;
  scheduleddatetime: string;
  date: string;
  starttime: string;
  duration: number;
  appointmenttype: string;
  appointmentstatus: string;
  athenaAppointmentId?: string;
  athenaPatientId?: string;
  athenaProviderId?: string;
  athenaDepartmentId?: string;
  appointmentTypeId?: string;
  startTime?: string;
  endTime?: string;
  durationMinutes?: number;
  reason?: string;
  reasonType?: string;
  status?: string;
  providerName?: string;
  departmentName?: string;
}

interface PatientData {
  athenaPatientId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  sex?: string;
  address?: string;
  zipCode?: string;
  existingPatient?: boolean;
  upcomingAppointments?: boolean;
}

// Enum for steps
enum Step {
  Landing,
  PatientStatus,
  ExistingAppointments,
  RescheduleAppointmentTypeZip,
  RescheduleProviderSelection,
  RescheduleExisting,
  AppointmentType,
  AppointmentTypeZip,
  ProviderSelection,
  Prequalification,
  PatientInfo,
  Confirmation,
  ManageAppointment,
  EditAppointment,
}

const AxisSchedulingRouter: React.FC = () => {
  const { providerIdentifier: paramsProviderIdentifier } = useParams<{ providerIdentifier?: string }>();
  
  // Extract appointment type and provider identifier from URL path
  const getAppointmentTypeAndProviderFromPath = () => {
    const path = window.location.pathname;
    
    // Check for Spravato routes
    const spravatoProviderMatch = path.match(/\/spravato\/provider\/([^/]+)/);
    if (spravatoProviderMatch) {
      return { appointmentType: 'spravato', providerIdentifier: spravatoProviderMatch[1] };
    }
    
    const spravatoMatch = path.match(/\/spravato\/?$/);
    if (spravatoMatch) {
      return { appointmentType: 'spravato', providerIdentifier: null };
    }
    
    // Check for regular provider routes
    const providerMatch = path.match(/\/provider\/([^/]+)/);
    if (providerMatch) {
      return { appointmentType: 'regular', providerIdentifier: providerMatch[1] };
    }
    
    return { appointmentType: 'regular', providerIdentifier: null };
  };
  
  const { appointmentType: urlAppointmentType, providerIdentifier: pathProviderIdentifier } = getAppointmentTypeAndProviderFromPath();
  const providerIdentifier = paramsProviderIdentifier || pathProviderIdentifier;
  
  // Providers that don't accept new patients for regular provider route
  const restrictedProviders = [
    'Dr_Ross_VanAllen_DNP',
    'Dr_Jamie_Teunis_DNP', 
    'Frances_Rhodes_NP',
    'Dr_Roderick_OBrienMD'
  ];
  const isRestrictedProvider = urlAppointmentType === 'regular' && providerIdentifier && restrictedProviders.includes(providerIdentifier);
  
  // URL parsing logic for appointment type and provider identification
  
  const [step, setStep] = useState<Step>(Step.Landing);
  // Placeholder for collected data
  const [appointmentType, setAppointmentType] = useState<'telehealth' | 'inperson' | 'spravato' | null>(null);
  const [zip, setZip] = useState('');
  const [radiusMiles, setRadiusMiles] = useState<number>(25);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [slot, setSlot] = useState<AppointmentSlot | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfoFormData | null>(null);
  const [prequalificationAnswers, setPrequalificationAnswers] = useState<Record<number, string>>({});
  const [success, setSuccess] = useState(false);
  const [editAppointment, setEditAppointment] = useState<ExistingAppointment | null>(null);
  const [patientStatus, setPatientStatus] = useState<'existing' | 'new' | null>(null);
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const [reschedulingAppointment, setReschedulingAppointment] = useState<ExistingAppointment | null>(null);
  const [initialUserChoice, setInitialUserChoice] = useState<'new' | 'existing' | null>(null);
  const [currentStartDate, setCurrentStartDate] = useState<Date>(() => {
    // Start with today + 2 days
    const today = new Date();
    today.setDate(today.getDate() + 2);
    return today;
  });

  // Reschedule flow state
  const [rescheduleAppointmentType, setRescheduleAppointmentType] = useState<'telehealth' | 'inperson' | 'spravato' | null>(null);
  const [rescheduleZip, setRescheduleZip] = useState('');
  const [rescheduleRadiusMiles, setRescheduleRadiusMiles] = useState<number>(25);
  const [rescheduleCurrentStartDate, setRescheduleCurrentStartDate] = useState<Date>(() => {
    // Start with today + 2 days
    const today = new Date();
    today.setDate(today.getDate() + 2);
    return today;
  });

  // Auto-set appointment type for Spravato routes
  React.useEffect(() => {
    if (urlAppointmentType === 'spravato') {
      setAppointmentType('spravato');
    }
  }, [urlAppointmentType]);

  // Step navigation handlers
  const goBack = () => setStep((prev) => (prev > 0 ? (prev - 1) as Step : prev));

  // Always call hooks at the top level
  const {
    data: rawOpenSlots = [],
    isLoading: providersLoading,
    error: providersError,
  } = useQuery(
    ['openSlots', appointmentType, zip, radiusMiles, patientStatus, providerIdentifier, currentStartDate],
    () => {
      const startDate = currentStartDate.toISOString().split('T')[0];
      
      const requestData: any = {
        reasonType: patientStatus === 'existing' ? 'existing' : 'new',
        reason: appointmentType === 'spravato' ? 'Spravato' : (appointmentType === 'telehealth' ? 'Telehealth' : 'In-Person'),
        startDate: startDate
      };

      // Add location parameters for in-person appointments (including Spravato)
      if ((appointmentType === 'inperson' || appointmentType === 'spravato') && zip) {
        requestData.zipCode = zip;
        requestData.radiusMiles = radiusMiles;
      }

      if (providerIdentifier) {
        return getProviderSpecificOpenSlots(providerIdentifier, requestData);
      }
      return getOpenSlots(requestData);
    },
    {
      enabled: step === Step.ProviderSelection && !!patientStatus && !isRestrictedProvider && (
        appointmentType === 'telehealth' || 
        (appointmentType === 'inperson' && !!zip && radiusMiles > 0) ||
        (appointmentType === 'spravato' && !!zip && radiusMiles > 0)
      ),
      onError: () => {
        // Error handling is managed by react-query and error boundaries
      }
    }
  );

  // Reschedule open slots query
  const {
    data: rawRescheduleOpenSlots = [],
    isLoading: rescheduleProvidersLoading,
    error: rescheduleProvidersError,
  } = useQuery(
    ['rescheduleOpenSlots', rescheduleAppointmentType, rescheduleZip, rescheduleRadiusMiles, patientStatus, providerIdentifier, rescheduleCurrentStartDate],
    () => {
      const startDate = rescheduleCurrentStartDate.toISOString().split('T')[0];
      const requestData: any = {
        reasonType: patientStatus === 'existing' ? 'existing' : 'new',
        reason: rescheduleAppointmentType === 'spravato' ? 'Spravato' : (rescheduleAppointmentType === 'telehealth' ? 'Telehealth' : 'In-Person'),
        startDate: startDate
      };

      // Add location parameters for in-person appointments (including Spravato)
      if ((rescheduleAppointmentType === 'inperson' || rescheduleAppointmentType === 'spravato') && rescheduleZip) {
        requestData.zipCode = rescheduleZip;
        requestData.radiusMiles = rescheduleRadiusMiles;
      }

      if (providerIdentifier) {
        return getProviderSpecificOpenSlots(providerIdentifier, requestData);
      }
      return getOpenSlots(requestData);
    },
    {
      enabled: step === Step.RescheduleProviderSelection && !!patientStatus && !isRestrictedProvider && (
        rescheduleAppointmentType === 'telehealth' || 
        (rescheduleAppointmentType === 'inperson' && !!rescheduleZip && rescheduleRadiusMiles > 0) ||
        (rescheduleAppointmentType === 'spravato' && !!rescheduleZip && rescheduleRadiusMiles > 0)
      ),
      onError: () => {
        // Error handling is managed by react-query and error boundaries
      }
    }
  );

  // Filter providers based on providerIdentifier if present
  const openSlots = React.useMemo(() => {
    // If it's a restricted provider, return empty array (no API call made)
    if (isRestrictedProvider) {
      return [];
    }
    
    if (!providerIdentifier || !rawOpenSlots?.length) {
      return rawOpenSlots;
    }

    // Filter providers that match the providerIdentifier
    const filteredProviders = rawOpenSlots.filter((provider: any) => {
      // Check if provider matches by scheduling name (like "Meredith_Grey")
      const schedulingName = provider.schedulingName || `${provider.firstName}_${provider.lastName}`;
      const providerId = provider.providerId;
      
      // Match by scheduling name (case-insensitive)
      const nameMatch = schedulingName.toLowerCase() === providerIdentifier.toLowerCase();
      // Match by provider ID if it's numeric
      const idMatch = providerId === providerIdentifier;
      
      return nameMatch || idMatch;
    });

    return filteredProviders;
  }, [rawOpenSlots, providerIdentifier, isRestrictedProvider]);

  // Filter reschedule providers based on providerIdentifier if present
  const rescheduleOpenSlots = React.useMemo(() => {
    // If it's a restricted provider, return empty array (no API call made)
    if (isRestrictedProvider) {
      return [];
    }
    
    if (!providerIdentifier || !rawRescheduleOpenSlots?.length) {
      return rawRescheduleOpenSlots;
    }

    // Filter providers that match the providerIdentifier
    const filteredProviders = rawRescheduleOpenSlots.filter((provider: any) => {
      // Check if provider matches by scheduling name (like "Meredith_Grey")
      const schedulingName = provider.schedulingName || `${provider.firstName}_${provider.lastName}`;
      const providerId = provider.providerId;
      
      // Match by scheduling name (case-insensitive)
      const nameMatch = schedulingName.toLowerCase() === providerIdentifier.toLowerCase();
      // Match by provider ID if it's numeric
      const idMatch = providerId === providerIdentifier;
      
      return nameMatch || idMatch;
    });

    return filteredProviders;
  }, [rawRescheduleOpenSlots, providerIdentifier, isRestrictedProvider]);

  // Step render logic
  switch (step) {
    case Step.Landing:
      if (success) return <SuccessStep onRestart={() => setSuccess(false)} />;
      return <LandingStep onSelect={(type) => {
        setInitialUserChoice(type);
        setStep(Step.PatientStatus);
      }} />;
    case Step.PatientStatus:
      return (
        <PatientStatusStep
          onProceed={(status: 'existing' | 'new', data: any) => {
            setPatientStatus(status);
            setPatientData(data);
            
            // Flow logic based on initial user choice and backend response
            if (initialUserChoice === 'existing') {
              // If backend confirms existing or has upcoming appointments, show existing appointments; otherwise go to selection
              if (status === 'existing' || data?.upcomingAppointments === true) {
                setStep(Step.ExistingAppointments);
              } else {
                // For existing patients without appointments, go to appointment type selection
                if (urlAppointmentType === 'spravato') {
                  setStep(Step.AppointmentTypeZip);
                } else {
                  setStep(Step.AppointmentTypeZip);
                }
              }
            } else if (initialUserChoice === 'new') {
              // Go directly to combined type + location screen
              if (urlAppointmentType === 'spravato') {
                setStep(Step.AppointmentTypeZip);
              } else {
                setStep(Step.AppointmentTypeZip);
              }
            } else {
              // Fallback - should not happen
              setStep(Step.AppointmentTypeZip);
            }
          }}
          onBack={() => setStep(Step.Landing)}
        />
      );
    case Step.ExistingAppointments:
      return patientData?.athenaPatientId ? (
        <ExistingAppointmentsStep
          onBack={() => setStep(Step.PatientStatus)}
          onReschedule={(appointment) => {
            setReschedulingAppointment(appointment);
            // For Spravato flow, go directly to location input; for regular flow, go to appointment type selection
            if (urlAppointmentType === 'spravato') {
              setRescheduleAppointmentType('spravato');
              setStep(Step.RescheduleAppointmentTypeZip);
            } else {
              setStep(Step.RescheduleAppointmentTypeZip);
            }
          }}
          onNewAppointment={() => setStep(Step.AppointmentTypeZip)}
          patientId={patientData.athenaPatientId}
          urlAppointmentType={urlAppointmentType as 'spravato' | 'regular'}
        />
      ) : null;
    case Step.RescheduleAppointmentTypeZip:
      return (
        <AppointmentTypeZipStep
          selectedType={rescheduleAppointmentType === 'spravato' ? 'inperson' : (rescheduleAppointmentType || 'telehealth')}
          onSelectType={(type) => setRescheduleAppointmentType(type)}
          zip={rescheduleZip}
          onZipChange={setRescheduleZip}
          radiusMiles={rescheduleRadiusMiles}
          onRadiusMilesChange={setRescheduleRadiusMiles}
          onProceed={() => setStep(Step.RescheduleProviderSelection)}
          onBack={() => setStep(Step.ExistingAppointments)}
          isSpravato={urlAppointmentType === 'spravato'}
        />
      );
    case Step.RescheduleProviderSelection:
      return (
        <ProviderSelectionStep
          onBack={() => setStep(Step.RescheduleAppointmentTypeZip)}
          onSelect={(prov: any, slot: any) => {
            setProvider(prov);
            setSlot(slot);
            setStep(Step.RescheduleExisting);
          }}
          onNewSearch={() => setStep(Step.ExistingAppointments)}
          providers={rescheduleOpenSlots}
          isLoading={rescheduleProvidersLoading}
          error={rescheduleProvidersError as string | null}
          reasonType={patientStatus === 'existing' ? 'existing' : 'new'}
          currentStartDate={rescheduleCurrentStartDate}
          onNextWeek={() => {
            const newDate = new Date(rescheduleCurrentStartDate);
            newDate.setDate(newDate.getDate() + 14);
            setRescheduleCurrentStartDate(newDate);
          }}
          onPreviousWeek={() => {
            const newDate = new Date(rescheduleCurrentStartDate);
            newDate.setDate(newDate.getDate() - 14);
            setRescheduleCurrentStartDate(newDate);
          }}
        />
      );
    case Step.RescheduleExisting:
      return reschedulingAppointment && patientData?.athenaPatientId && provider && slot ? (
        <ConfirmationStep
          onBack={() => setStep(Step.RescheduleProviderSelection)}
          onSuccess={() => {
            setReschedulingAppointment(null);
            setProvider(null);
            setSlot(null);
            setStep(Step.Landing);
          }}
          provider={provider}
          slot={slot}
          appointmentType={rescheduleAppointmentType === 'spravato' ? 'spravato' : (rescheduleAppointmentType === 'telehealth' ? 'telehealth' : 'inperson')}
          patientInfo={null}
          patientStatus="existing"
          patientData={patientData}
          reschedulingAppointment={reschedulingAppointment}
        />
      ) : null;
    case Step.AppointmentType:
      return <AppointmentTypeStep onSelect={(type) => { setAppointmentType(type); setStep(Step.AppointmentTypeZip); }} onBack={() => setStep(Step.Landing)} />;
    case Step.AppointmentTypeZip:
      return (
        <AppointmentTypeZipStep
          selectedType={appointmentType === 'spravato' ? 'inperson' : (appointmentType ?? 'telehealth')}
          onSelectType={setAppointmentType}
          zip={zip}
          onZipChange={setZip}
          radiusMiles={radiusMiles}
          onRadiusMilesChange={setRadiusMiles}
          isSpravato={urlAppointmentType === 'spravato'}
          onProceed={() => {
            // For Spravato, ensure appointment type is set to spravato
            if (urlAppointmentType === 'spravato') {
              setAppointmentType('spravato');
            } else if (!appointmentType) {
              // Default to telehealth if user doesn't toggle cards explicitly
              setAppointmentType('telehealth');
            }
            setStep(Step.ProviderSelection);
          }}
          onBack={() => setStep(Step.PatientStatus)}
        />
      );
    case Step.ProviderSelection:
      return appointmentType ? (
        <ProviderSelectionStep
          onBack={() => setStep(Step.AppointmentTypeZip)}
          onSelect={(prov: any, slot: any) => { 
            setProvider(prov); 
            setSlot(slot); 
            // Skip prequalification for Spravato appointments
            if (urlAppointmentType === 'spravato') {
              setStep(Step.PatientInfo);
            } else {
              setStep(Step.Prequalification);
            }
          }}
          onNewSearch={() => setStep(Step.Landing)}
          providers={openSlots}
          isLoading={providersLoading}
          error={providersError as string | null}
          reasonType={patientStatus}
          isRestrictedProvider={!!isRestrictedProvider}
          currentStartDate={currentStartDate}
          onNextWeek={() => {
            const newDate = new Date(currentStartDate);
            newDate.setDate(newDate.getDate() + 14);
            setCurrentStartDate(newDate);
          }}
          onPreviousWeek={() => {
            const newDate = new Date(currentStartDate);
            newDate.setDate(newDate.getDate() - 14);
            setCurrentStartDate(newDate);
          }}
        />
      ) : null;
    case Step.Prequalification:
      return (
        <PrequalificationStep 
          onBack={goBack} 
          onProceed={() => setStep(Step.PatientInfo)}
          initialAnswers={prequalificationAnswers}
          onAnswersChange={setPrequalificationAnswers}
        />
      );
    case Step.PatientInfo:
      return (
        <PatientInfoStep 
          onBack={goBack} 
          onProceed={(formData) => { setPatientInfo(formData); setStep(Step.Confirmation); }}
          initialData={patientInfo}
          patientData={patientData}
          isSpravato={appointmentType === 'spravato'}
        />
      );
    case Step.Confirmation:
      return provider && slot ? (
        <ConfirmationStep
          onBack={goBack}
          provider={provider}
          slot={slot}
          appointmentType={appointmentType}
          patientInfo={patientInfo}
          patientStatus={patientStatus || undefined}
          patientData={patientData}
          reschedulingAppointment={reschedulingAppointment}
          onSuccess={() => {
            setSuccess(true);
            setStep(Step.Landing);
            setAppointmentType(null);
            setProvider(null);
            setSlot(null);
            setPatientInfo(null);
            setPrequalificationAnswers({});
            setZip('');
            setPatientStatus(null);
            setPatientData(null);
            setReschedulingAppointment(null);
            setInitialUserChoice(null);
          }}
        />
      ) : null;
    case Step.ManageAppointment:
      return <ManageAppointmentStep onBack={() => setStep(Step.Landing)} onDone={() => setStep(Step.Landing)} onEdit={(appt) => { setEditAppointment(appt); setStep(Step.EditAppointment); }} />;
    case Step.EditAppointment:
      return <EditAppointmentRouter appointment={editAppointment} onDone={() => setStep(Step.Landing)} />;
    default:
      return null;
  }
};

export default AxisSchedulingRouter; 